import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface ChatWidgetContextType {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

const ChatWidgetContext = createContext<ChatWidgetContextType | undefined>(undefined);

export function ChatWidgetProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState<boolean>(() => {
    try {
      return localStorage.getItem('chatbox:open') === '1';
    } catch {
      return false;
    }
  });

  const handleSetVisible = useCallback((v: boolean) => {
    setVisible(v);
    try {
      localStorage.setItem('chatbox:open', v ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(() => ({ visible, setVisible: handleSetVisible }), [visible, handleSetVisible]);

  return <ChatWidgetContext.Provider value={value}>{children}</ChatWidgetContext.Provider>;
}

export function useChatWidgetVisibility() {
  const context = useContext(ChatWidgetContext);
  if (!context) {
    throw new Error('useChatWidgetVisibility must be used within ChatWidgetProvider');
  }
  return context;
}
